// Modal edycji komentarza do pozycji oferty (tylko treść + widoczność dla klienta).

import React, { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, CircularProgress, FormControlLabel, TextField } from '@mui/material';
import { useQueryClient } from 'react-query';

import { WiseModal } from 'components/wise';
import { usePutSalesCrmOfferPositionComment } from 'api';

import styles from 'theme/components/containers/TraderClientOfferProductsContainer/AddPositionCommentModal.module.scss';

interface IEditPositionCommentModalProps {
  open: boolean;
  clientId: number;
  offerId: string;
  positionId: number;
  commentId: number;
  initialComment: string;
  initialIsVisibleForClient: boolean;
  /** Typ autora komentarza - dla 'CLIENT' blokujemy zmiane widocznosci (handlowiec nie moze ukryc komentarza klienta). */
  authorType?: 'CLIENT' | 'TRADER' | null;
  onClose: () => void;
}

const EditPositionCommentModal: FC<IEditPositionCommentModalProps> = ({
  open,
  clientId,
  offerId,
  positionId,
  commentId,
  initialComment,
  initialIsVisibleForClient,
  authorType,
  onClose
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [comment, setComment] = useState(initialComment);
  const [isVisible, setIsVisible] = useState(initialIsVisibleForClient);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setComment(initialComment);
      setIsVisible(initialIsVisibleForClient);
      setErrorMessage(null);
    }
  }, [open, initialComment, initialIsVisibleForClient]);

  const { mutate: putComment, isLoading } = usePutSalesCrmOfferPositionComment(
    clientId,
    offerId,
    positionId,
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'sales-crm',
          'client',
          clientId,
          'offer',
          offerId,
          'position',
          positionId,
          'comments'
        ]);
        onClose();
      },
      onError: (error) => {
        setErrorMessage(error?.message || t('Nie udało się zapisać komentarza.'));
      }
    }
  );

  const handleClose = () => {
    setErrorMessage(null);
    onClose();
  };

  const handleSubmit = () => {
    const trimmed = comment.trim();
    if (!trimmed) {
      setErrorMessage(t('Treść komentarza jest wymagana.'));
      return;
    }
    setErrorMessage(null);
    putComment({
      commentId,
      payload: { comment: trimmed, isVisibleForClient: isVisible }
    });
  };

  return (
    <WiseModal
      open={open}
      onClose={handleClose}
      title={t('Edytuj komentarz')}
      maxWidth={520}>
      <div className={styles.body}>
        <TextField
          label={t('Treść komentarza')}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          multiline
          minRows={4}
          fullWidth
          autoFocus
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={isVisible}
              onChange={(e) => setIsVisible(e.target.checked)}
              disabled={authorType === 'CLIENT'}
              sx={{
                color: '#6b54ff',
                '&.Mui-checked': { color: '#6b54ff' }
              }}
            />
          }
          label={t('Widoczny dla klienta')}
          title={
            authorType === 'CLIENT'
              ? t('Komentarze klienta sa zawsze widoczne dla obu stron - nie mozna zmienic widocznosci.') || ''
              : undefined
          }
        />

        {errorMessage && <div className={styles.error}>{errorMessage}</div>}

        <div className={styles.actions}>
          <Button onClick={handleClose} variant="text">
            {t('Anuluj')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isLoading || !comment.trim()}
            sx={{ backgroundColor: '#6b54ff', '&:hover': { backgroundColor: '#5a47e0' } }}>
            {isLoading ? <CircularProgress size={18} /> : t('Zapisz')}
          </Button>
        </div>
      </div>
    </WiseModal>
  );
};

export default EditPositionCommentModal;
